import {
    AppFlyoutAnchor,
    getActionSourceFromCommandingViewMode,
    getKeytipString,
    constructKeytip,
} from 'owa-command-ribbon';
import loc from 'owa-localize';
import { listViewStore, MailRowDataPropertyGetter, TableView } from 'owa-mail-list-store';
import compareTagsByRetentionPeriod from 'owa-mail-retention-policies/lib/helpers/getComparatorTagsByRetentionPeriod';
import getFolderTypeFromDistinguishedFolderId from 'owa-mail-retention-policies/lib/helpers/getFolderTypeFromDistinguishedFolderId';
import getIsArchivePolicyEnabled from 'owa-mail-retention-policies/lib/helpers/getIsArchivePolicyEnabled';
import buildHierarchyPolicyTags from 'owa-mail-retention-policies/lib/utils/buildHierarchyPolicyTags';
import { useParentFolderPolicyText } from 'owa-mail-retention-policies/lib/utils/createInheritSubMenuItem.locstring.json';
import { archivePolicyMenuGroupHeaderText } from 'owa-mail-retention-policies/lib/utils/generateArchivePolicyList.locstring.json';
import { deletePolicyMenuGroupHeaderText } from 'owa-mail-retention-policies/lib/utils/generateDeletePolicyList.locstring.json';
import {
    getRetentionPolicyTagList,
    getRetentionPolicyTagListForItem,
} from 'owa-mail-retention-policies/lib/utils/getRetentionPolicyTagList';
import { lazyOnAssignPolicy } from 'owa-mail-retention-policies';
import { getStringRibbonId, MailRibbonTabId, MailRibbonControlId } from 'owa-ribbon-ids';
import { ControlInfo, getControlInfo } from 'owa-mail-ribbon-utils';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import ElcFolderType from 'owa-service/lib/contract/ElcFolderType';
import {
    createMailAppButton,
    createMailGeneratedMenuItem,
} from '../../util/createMailRibbonButton';
import type { HierarchyRetentionPolicyTag } from 'owa-mail-retention-policies/lib/utils/createHierarchialSubMenuDeleteItem';
import getFolderInfoForRetentionPolicy, {
    FolderInfo,
    getFolderInfoFromItemForRetentionPolicy,
} from 'owa-mail-retention-policies/lib/utils/getFolderInfoForRetentionPolicy';
import {
    ARCHIVE_INHERIT_SUBMENU_ITEM_IDENTIFIER,
    DELETE_INHERIT_SUBMENU_ITEM_IDENTIFIER,
    DISTINGUISHED_FOLDER_ID_FOR_CONTEXTUAL_MENUS,
} from 'owa-mail-retention-policies/lib/utils/retentionPolicyConstants';
import type RetentionPolicyTag from 'owa-service/lib/contract/RetentionPolicyTag';
import type DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import type RecursiveReadOnly from 'owa-service/lib/RecursiveReadOnly';
import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import type { RibbonFlyoutAnchorProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import type { ClientItemId } from 'owa-client-ids';
import { ClientItem, getStore as getMailStore } from 'owa-mail-store';
import type RetentionTagType from 'owa-service/lib/contract/RetentionTagType';
import { getReadRibbonId } from '../../util/getReadRibbonId';

/**
 * This function returns the drop-down content of the "Assign Policy" button in the Ribbon.
 * There are 2 sections here:
 *      1) Archive Policies
 *      2) Retention (Delete) Policies
 *
 * @param tabId is the tab that this sections is being called from. Important for keytips.
 */
export default function getAssignPolicySections(
    tabId?: MailRibbonTabId,
    itemId?: ClientItemId
): MenuSection[] {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    const selectedRowKeys = [...tableView.selectedRowKeys.keys()];

    let folderInfo: FolderInfo;
    let item;
    if (itemId) {
        item = getMailStore().items.get(itemId.Id);
        if (!item) {
            return [];
        }
        folderInfo = getFolderInfoFromItemForRetentionPolicy(item);
    } else {
        folderInfo = getFolderInfoForRetentionPolicy(tableView);
    }

    if (!folderInfo) {
        return [];
    }
    let retentionPolicyTagList;
    if (item) {
        retentionPolicyTagList = getRetentionPolicyTagListForItem(
            folderInfo.mailboxType,
            folderInfo.principalSMTPAddress,
            folderInfo.folderId
        );
    } else {
        retentionPolicyTagList = getRetentionPolicyTagList(
            tableView,
            folderInfo.mailboxType,
            folderInfo.principalSMTPAddress
        );
    }
    if (!retentionPolicyTagList) {
        return [];
    }
    const folderType = getFolderTypeFromDistinguishedFolderId(
        <DistinguishedFolderIdName>DISTINGUISHED_FOLDER_ID_FOR_CONTEXTUAL_MENUS
    );

    return [
        {
            title: loc(archivePolicyMenuGroupHeaderText),
            controls: getArchivePolicySection(
                tableView,
                selectedRowKeys,
                folderInfo,
                folderType,
                retentionPolicyTagList,
                tabId,
                item
            ),
        },
        {
            title: loc(deletePolicyMenuGroupHeaderText),
            controls: getDeletePolicySection(
                tableView,
                selectedRowKeys,
                folderInfo,
                folderType,
                retentionPolicyTagList,
                tabId,
                item
            ),
        },
        {
            controls: getMoreRetentionPoliciesOptionsControls(tabId),
        },
    ];
}

function getMoreRetentionPoliciesOptionsControls(tabId?: MailRibbonTabId): MenuItemControlProps[] {
    const controls: MenuItemControlProps[] = [];
    const controlInfo: ControlInfo = getControlInfo(MailRibbonControlId.MoreRetentionPolicies);

    controls.push(
        createMailAppButton(
            MailRibbonControlId.MoreRetentionPolicies,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            () => {
                lazyMountAndShowFullOptions.importAndExecute('mail', 'retentionPolicies');
            },
            constructKeytip(
                tabId
                    ? [getKeytipString(tabId), getKeytipString(MailRibbonControlId.AssignPolicy)]
                    : [getKeytipString(MailRibbonControlId.AssignPolicy)],
                getKeytipString(MailRibbonControlId.MoreRetentionPolicies)
            ),
            controlInfo.iconName
        )
    );

    return controls;
}

function getArchivePolicySection(
    tableView: TableView,
    selectedRowKeys: string[],
    folderInfo: FolderInfo,
    folderType: ElcFolderType,
    retentionPolicyTagList: readonly RecursiveReadOnly<RetentionPolicyTag>[],
    tabId?: MailRibbonTabId,
    item?: ClientItem
): MenuItemControlProps[] {
    let sections: MenuItemControlProps[] = [];

    // Decide whether to show Archive Policies
    if (
        !getIsArchivePolicyEnabled(tableView, item) ||
        folderInfo.mailboxType === 'ArchiveMailbox'
    ) {
        return [];
    }

    let selectedArchiveTag: RetentionTagType | null;
    let currentArchivePolicyValue: string | null;
    if (item) {
        selectedArchiveTag = item.ArchiveTag ?? null;
        currentArchivePolicyValue = selectedArchiveTag?.IsExplicit
            ? selectedArchiveTag.Value
            : null;
    } else {
        selectedArchiveTag =
            selectedRowKeys.length === 1
                ? MailRowDataPropertyGetter.getArchiveTag(selectedRowKeys[0], tableView)
                : null;
        currentArchivePolicyValue =
            selectedArchiveTag?.IsExplicit && selectedRowKeys.length === 1
                ? selectedArchiveTag.Value
                : null;
    }
    const visibleArchiveTags = retentionPolicyTagList
        .filter(tag => tag.IsVisible && tag.IsArchive)
        .sort(compareTagsByRetentionPeriod);

    // If it's a root folder, we do not show personal archive policy options, only default archive policy
    let selectedPolicyFound = false;
    if (folderType != ElcFolderType.All) {
        visibleArchiveTags.map(tag => {
            const isSelected = tag.RetentionId === currentArchivePolicyValue;
            if (isSelected) {
                selectedPolicyFound = true;
            }
            sections.push(
                createMailGeneratedMenuItem(
                    getReadRibbonId('AssignArchivePolicy_' + tag.DisplayName, item?.ItemId),
                    tag.DisplayName,
                    undefined /*tooltip*/,
                    () =>
                        lazyOnAssignPolicy.import().then(onAssignPolicy => {
                            onAssignPolicy(
                                tag.RetentionId,
                                true /* isArchivePolicyClicked */,
                                selectedArchiveTag,
                                folderInfo.folderId,
                                item /* clientItem */,
                                getActionSourceFromCommandingViewMode()
                            );
                        }),
                    undefined /* keytipProps */,
                    isSelected
                )
            );
        });
    }

    // Push in "use parent folder policy" button
    sections.push(
        createMailGeneratedMenuItem(
            getReadRibbonId(MailRibbonControlId.AssignPolicyArchiveInherit, item?.ItemId),
            loc(useParentFolderPolicyText),
            undefined /*tooltip*/,
            () =>
                lazyOnAssignPolicy.import().then(onAssignPolicy => {
                    onAssignPolicy(
                        ARCHIVE_INHERIT_SUBMENU_ITEM_IDENTIFIER,
                        true /* isArchivePolicyClicked */,
                        selectedArchiveTag,
                        folderInfo.folderId,
                        item /* clientItem */,
                        getActionSourceFromCommandingViewMode()
                    );
                }),
            constructKeytip(
                tabId
                    ? [getKeytipString(tabId), getKeytipString(MailRibbonControlId.AssignPolicy)]
                    : [getKeytipString(MailRibbonControlId.AssignPolicy)],
                getKeytipString(MailRibbonControlId.AssignPolicyArchiveInherit)
            ),
            !selectedPolicyFound && selectedRowKeys.length === 1
        )
    );

    return sections;
}

function getDeletePolicySection(
    tableView: TableView,
    selectedRowKeys: string[],
    folderInfo: FolderInfo,
    folderType: ElcFolderType,
    retentionPolicyTagList: readonly RecursiveReadOnly<RetentionPolicyTag>[],
    tabId?: MailRibbonTabId,
    item?: ClientItem
): (MenuItemControlProps | RibbonFlyoutAnchorProps)[] {
    let sections: (MenuItemControlProps | RibbonFlyoutAnchorProps)[] = [];

    let selectedDeleteTags: RetentionTagType[] | null;
    let currentDeleteTagValue: string | null;
    if (item) {
        selectedDeleteTags = item.PolicyTag ? [item.PolicyTag] : null;
        currentDeleteTagValue = item.PolicyTag?.Value ?? null;
    } else {
        selectedDeleteTags = selectedRowKeys.map(selectedRowKey =>
            MailRowDataPropertyGetter.getPolicyTag(selectedRowKey, tableView)
        );
        currentDeleteTagValue =
            selectedRowKeys.length === 1 && selectedDeleteTags[0]?.IsExplicit
                ? selectedDeleteTags[0].Value
                : null;
    }

    const visibleDeleteTags = retentionPolicyTagList
        .filter(tag => tag.IsVisible && !tag.IsArchive)
        .sort(compareTagsByRetentionPeriod);

    // Only allow applying personal delete policy on personal folders
    let selectedPolicyFound = false;
    if (folderType == ElcFolderType.Personal) {
        buildHierarchyPolicyTags(visibleDeleteTags).map(
            (hierarchyTag: HierarchyRetentionPolicyTag) => {
                const { parentPolicyTag, childrenPolicyTags } = hierarchyTag;
                const isRootSelected = parentPolicyTag.RetentionId === currentDeleteTagValue;
                if (isRootSelected) {
                    selectedPolicyFound = true;
                }

                // If childern tags do not exist, create menu item with the parent itself
                if (childrenPolicyTags == null || childrenPolicyTags.length == 0) {
                    sections.push(
                        createMailGeneratedMenuItem(
                            getReadRibbonId(
                                'AssignDeletePolicy_' + parentPolicyTag.DisplayName,
                                item?.ItemId
                            ),
                            parentPolicyTag.DisplayName,
                            undefined /*tooltip*/,
                            () =>
                                lazyOnAssignPolicy.import().then(onAssignPolicy => {
                                    onAssignPolicy(
                                        parentPolicyTag.RetentionId,
                                        false /* isArchivePolicyClicked */,
                                        selectedDeleteTags,
                                        folderInfo.folderId,
                                        item /* clientItem */,
                                        getActionSourceFromCommandingViewMode()
                                    );
                                }),
                            undefined /* keytipProps */,
                            isRootSelected
                        )
                    );
                } else {
                    // Otherwise, create a flyout menu list of child menu items for the parent tag
                    let childPolicyFlyoutSection: MenuItemControlProps[] = [];
                    childrenPolicyTags.map((childTag: RetentionPolicyTag) => {
                        const isChildSelected = childTag.RetentionId === currentDeleteTagValue;
                        if (isChildSelected) {
                            selectedPolicyFound = true;
                        }
                        childPolicyFlyoutSection.push(
                            createMailGeneratedMenuItem(
                                getReadRibbonId(
                                    'AssignDeletePolicy_' + childTag.DisplayName,
                                    item?.ItemId
                                ),
                                childTag.DisplayName,
                                undefined /*tooltip*/,
                                () =>
                                    lazyOnAssignPolicy.import().then(onAssignPolicy => {
                                        onAssignPolicy(
                                            childTag.RetentionId,
                                            false /* isArchivePolicyClicked */,
                                            selectedDeleteTags,
                                            folderInfo.folderId,
                                            item /* clientItem */,
                                            getActionSourceFromCommandingViewMode()
                                        );
                                    }),
                                undefined /* keytipProps */
                            )
                        );

                        // Push and create the flyout
                        sections.push({
                            type: AppFlyoutAnchor,
                            id: getStringRibbonId(MailRibbonControlId.AssignPolicyParentTagFlyout),
                            label: parentPolicyTag.DisplayName,
                            menuDefinition: {
                                sections: [{ controls: childPolicyFlyoutSection }],
                            },
                        });
                    });
                }
            }
        );
    }

    // Push in "use parent folder policy" button
    sections.push(
        createMailGeneratedMenuItem(
            getReadRibbonId(MailRibbonControlId.AssignPolicyDeleteInherit, item?.ItemId),
            loc(useParentFolderPolicyText),
            undefined /*tooltip*/,
            () =>
                lazyOnAssignPolicy.import().then(onAssignPolicy => {
                    onAssignPolicy(
                        DELETE_INHERIT_SUBMENU_ITEM_IDENTIFIER,
                        false /* isArchivePolicyClicked */,
                        selectedDeleteTags,
                        folderInfo.folderId,
                        item /* clientItem */,
                        getActionSourceFromCommandingViewMode()
                    );
                }),
            constructKeytip(
                tabId
                    ? [getKeytipString(tabId), getKeytipString(MailRibbonControlId.AssignPolicy)]
                    : [getKeytipString(MailRibbonControlId.AssignPolicy)],
                getKeytipString(MailRibbonControlId.AssignPolicyDeleteInherit)
            ),
            !selectedPolicyFound && selectedRowKeys.length === 1
        )
    );

    return sections;
}
